import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  CircularProgress,
  InputAdornment,
  FormHelperText,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SaveIcon from '@material-ui/icons/Save';
import transactionService from '../../services/transactionService';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  }
}));

const TransactionCreatePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [formData, setFormData] = useState({
    reference: `PAY-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
    type: 'payment',
    bookingId: '',
    bookingReference: '',
    userId: '',
    userName: '',
    partnerId: '',
    partnerName: '',
    amount: '',
    currency: 'EUR',
    status: 'pending',
    paymentMethod: 'card',
    cardLast4: '',
    description: '',
  });
  
  const [errors, setErrors] = useState({});
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    fetchRelatedData();
  }, []);

  useEffect(() => {
    // Mettre à jour la référence en fonction du type
    if (formData.type === 'payment') {
      setFormData({
        ...formData,
        reference: `PAY-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`
      });
    } else if (formData.type === 'refund') {
      setFormData({
        ...formData,
        reference: `REF-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`
      });
    } else if (formData.type === 'commission_payment') {
      setFormData({
        ...formData,
        reference: `COM-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`
      });
    }
  }, [formData.type]);

  const fetchRelatedData = async () => {
    setLoading(true);
    try {
      // Simulation de récupération des données depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par des appels à l'API
      
      // Données de démonstration pour les réservations
      const demoBookings = [
        { id: 'B001', reference: 'RES-2025-001', userId: 'U001', userName: 'Alice Martin', amount: 120.50 },
        { id: 'B002', reference: 'RES-2025-002', userId: 'U002', userName: 'Thomas Bernard', amount: 85.75 },
        { id: 'B003', reference: 'RES-2025-003', userId: 'U003', userName: 'Sophie Dubois', amount: 145.20 },
        { id: 'B004', reference: 'RES-2025-004', userId: 'U004', userName: 'Pierre Lambert', amount: 175.00 },
        { id: 'B005', reference: 'RES-2025-005', userId: 'U005', userName: 'Julie Moreau', amount: 65.30 },
      ];
      setBookings(demoBookings);
      
      // Données de démonstration pour les utilisateurs
      const demoUsers = [
        { id: 'U001', name: 'Alice Martin', email: 'alice.martin@example.com' },
        { id: 'U002', name: 'Thomas Bernard', email: 'thomas.bernard@example.com' },
        { id: 'U003', name: 'Sophie Dubois', email: 'sophie.dubois@example.com' },
        { id: 'U004', name: 'Pierre Lambert', email: 'pierre.lambert@example.com' },
        { id: 'U005', name: 'Julie Moreau', email: 'julie.moreau@example.com' },
      ];
      setUsers(demoUsers);
      
      // Données de démonstration pour les partenaires
      const demoPartners = [
        { id: 'P001', name: 'Entreprise ABC', email: 'contact@abc.com' },
        { id: 'P002', name: 'Société XYZ', email: 'info@xyz.com' },
        { id: 'P003', name: 'Compagnie 123', email: 'support@123.com' },
      ];
      setPartners(demoPartners);
      
    } catch (error) {
      console.error('Error fetching related data:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données associées',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'bookingId' && value) {
      const selectedBooking = bookings.find(booking => booking.id === value);
      if (selectedBooking) {
        setFormData({
          ...formData,
          [name]: value,
          bookingReference: selectedBooking.reference,
          userId: selectedBooking.userId,
          userName: selectedBooking.userName,
          amount: selectedBooking.amount.toString()
        });
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    } else if (name === 'userId' && value) {
      const selectedUser = users.find(user => user.id === value);
      if (selectedUser) {
        setFormData({
          ...formData,
          [name]: value,
          userName: selectedUser.name
        });
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    } else if (name === 'partnerId' && value) {
      const selectedPartner = partners.find(partner => partner.id === value);
      if (selectedPartner) {
        setFormData({
          ...formData,
          [name]: value,
          partnerName: selectedPartner.name
        });
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Effacer l'erreur lorsque l'utilisateur modifie le champ
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleCancel = () => {
    navigate('/admin/transactions');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.amount) {
      newErrors.amount = 'Le montant est requis';
    } else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Le montant doit être un nombre positif';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }
    
    if (formData.type === 'payment' || formData.type === 'refund') {
      if (!formData.bookingId) {
        newErrors.bookingId = 'La réservation est requise';
      }
      if (!formData.userId) {
        newErrors.userId = 'L\'utilisateur est requis';
      }
    } else if (formData.type === 'commission_payment') {
      if (!formData.partnerId) {
        newErrors.partnerId = 'Le partenaire est requis';
      }
    }
    
    if (formData.paymentMethod === 'card' && !formData.cardLast4) {
      newErrors.cardLast4 = 'Les 4 derniers chiffres de la carte sont requis';
    } else if (formData.paymentMethod === 'card' && !/^\d{4}$/.test(formData.cardLast4)) {
      newErrors.cardLast4 = 'Doit contenir exactement 4 chiffres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Veuillez corriger les erreurs dans le formulaire',
        severity: 'error'
      });
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Préparer les données pour l'envoi
      const transactionData = {
        ...formData,
        amount: parseFloat(formData.amount)
      };
      
      // Supprimer les champs non pertinents en fonction du type
      if (formData.type === 'commission_payment') {
        delete transactionData.bookingId;
        delete transactionData.bookingReference;
        delete transactionData.userId;
        delete transactionData.userName;
      } else {
        delete transactionData.partnerId;
        delete transactionData.partnerName;
      }
      
      if (formData.paymentMethod !== 'card') {
        delete transactionData.cardLast4;
      }
      
      // Créer la transaction via le service
      await transactionService.createTransaction(transactionData);
      
      setSnackbar({
        open: true,
        message: 'Transaction créée avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste des transactions après un court délai
      setTimeout(() => {
        navigate('/admin/transactions');
      }, 1500);
      
    } catch (error) {
      console.error('Error creating transaction:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la création de la transaction',
        severity: 'error'
      });
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleCancel}
        style={{ marginBottom: 16 }}
      >
        Retour à la liste
      </Button>
      
      <Typography variant="h4" component="h1" gutterBottom>
        Créer une nouvelle transaction
      </Typography>
      
      <Paper className={classes.paper}>
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : (
          <form className={classes.form} onSubmit={handleSubmit}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Informations de base
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="reference"
                  label="Référence"
                  variant="outlined"
                  fullWidth
                  value={formData.reference}
                  disabled
                  helperText="Générée automatiquement"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="type-label">Type de transaction</InputLabel>
                  <Select
                    labelId="type-label"
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    label="Type de transaction"
                  >
                    <MenuItem value="payment">Paiement</MenuItem>
                    <MenuItem value="refund">Remboursement</MenuItem>
                    <MenuItem value="commission_payment">Paiement de commission</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Divider className={classes.divider} />
            
            {(formData.type === 'payment' || formData.type === 'refund') && (
              <>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Détails de la réservation et du client
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl variant="outlined" className={classes.formControl} error={!!errors.bookingId}>
                      <InputLabel id="booking-label">Réservation</InputLabel>
                      <Select
                        labelId="booking-label"
                        id="bookingId"
                        name="bookingId"
                        value={formData.bookingId}
                        onChange={handleChange}
                        label="Réservation"
                        required
                      >
                        <MenuItem value="">
                          <em>Sélectionner une réservation</em>
                        </MenuItem>
                        {bookings.map((booking) => (
                          <MenuItem key={booking.id} value={booking.id}>
                            {booking.reference} - {booking.userName}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.bookingId && <FormHelperText>{errors.bookingId}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl variant="outlined" className={classes.formControl} error={!!errors.userId}>
                      <InputLabel id="user-label">Client</InputLabel>
                      <Select
                        labelId="user-label"
                        id="userId"
                        name="userId"
                        value={formData.userId}
                        onChange={handleChange}
                        label="Client"
                        required
                      >
                        <MenuItem value="">
                          <em>Sélectionner un client</em>
                        </MenuItem>
                        {users.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.name} ({user.email})
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.userId && <FormHelperText>{errors.userId}</FormHelperText>}
                    </FormControl>
                  </Grid>
                </Grid>
              </>
            )}
            
            {formData.type === 'commission_payment' && (
              <>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Détails du partenaire
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl variant="outlined" className={classes.formControl} error={!!errors.partnerId}>
                      <InputLabel id="partner-label">Partenaire</InputLabel>
                      <Select
                        labelId="partner-label"
                        id="partnerId"
                        name="partnerId"
                        value={formData.partnerId}
                        onChange={handleChange}
                        label="Partenaire"
                        required
                      >
                        <MenuItem value="">
                          <em>Sélectionner un partenaire</em>
                        </MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner.id} value={partner.id}>
                            {partner.name} ({partner.email})
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.partnerId && <FormHelperText>{errors.partnerId}</FormHelperText>}
                    </FormControl>
                  </Grid>
                </Grid>
              </>
            )}
            
            <Divider className={classes.divider} />
            
            <Typography variant="h6" className={classes.sectionTitle}>
              Détails financiers
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="amount"
                  label="Montant"
                  variant="outlined"
                  fullWidth
                  required
                  type="number"
                  value={formData.amount}
                  onChange={handleChange}
                  error={!!errors.amount}
                  helperText={errors.amount}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                  inputProps={{
                    min: 0.01,
                    step: 0.01
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="currency-label">Devise</InputLabel>
                  <Select
                    labelId="currency-label"
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    label="Devise"
                  >
                    <MenuItem value="EUR">Euro (€)</MenuItem>
                    <MenuItem value="USD">Dollar américain ($)</MenuItem>
                    <MenuItem value="GBP">Livre sterling (£)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="status-label">Statut</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Statut"
                  >
                    <MenuItem value="pending">En attente</MenuItem>
                    <MenuItem value="completed">Terminée</MenuItem>
                    <MenuItem value="failed">Échouée</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="payment-method-label">Méthode de paiement</InputLabel>
                  <Select
                    labelId="payment-method-label"
                    id="paymentMethod"
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                    label="Méthode de paiement"
                  >
                    <MenuItem value="card">Carte bancaire</MenuItem>
                    <MenuItem value="paypal">PayPal</MenuItem>
                    <MenuItem value="bank_transfer">Virement bancaire</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {formData.paymentMethod === 'card' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    name="cardLast4"
                    label="4 derniers chiffres de la carte"
                    variant="outlined"
                    fullWidth
                    required
                    value={formData.cardLast4}
                    onChange={handleChange}
                    error={!!errors.cardLast4}
                    helperText={errors.cardLast4 || "Ex: 4242"}
                    inputProps={{
                      maxLength: 4,
                      pattern: '[0-9]*'
                    }}
                  />
                </Grid>
              )}
              
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Description"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.description}
                  onChange={handleChange}
                  error={!!errors.description}
                  helperText={errors.description}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
            
            <div className={classes.buttons}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCancel}
                className={classes.button}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.button}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={24} /> : <SaveIcon />}
              >
                {submitting ? 'Création...' : 'Créer'}
              </Button>
            </div>
          </form>
        )}
      </Paper>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TransactionCreatePage;
